import { useLayoutEffect, useState } from 'react'
import { Trans } from 'react-i18next'

import { COND_FEATURE_FLAG_ALL, COND_FEATURE_FLAG_ALL_WITH_FORM, ConditionValue } from '@makyoapp/makyo-common'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useBuilderAccordionContext } from '@/context/__project__/BuilderAccordionContext'
import { useUpdateCompSettingValue } from '@/hooks/useUpdateCompSettings'
import {
  BuilderComponent,
  BuilderComponentSetting,
  BuilderDependOn,
  ComponentBehaviorKey,
  FlattenedItem,
} from '@/shared/builder'
import { Accordion, AccordionItem } from '@/components/Accordion/Accordion'
import { VisibilitySelector } from '@/components/BehaviourV2/VisibilitySelector'

import ComponentSettingsAccordion from './ComponentSettingsAccordion'

interface IComponentBehaviorPanel {
  comp: BuilderComponent
  flattenedSettings: BuilderComponentSetting[]
  flattenedComponents: FlattenedItem[]
  settings?: {
    custom?: Record<
      string,
      {
        dependOn?: BuilderDependOn
        items: BuilderComponentSetting[]
      }
    >
    visibility: boolean
  }
  ancestors: FlattenedItem[]
}

/**
 * The ComponentBehaviorPanel function is a component that provides an interface for editing the behavior settings of a component in a builder.
 *
 * @param {object} props - The props that define the component's behavior and state.
 * @param {BuilderComponent} props.comp - The component object that contains the details of the component being edited.
 * @param {BuilderComponentSetting[]} props.flattenedSettings - An array of flattened settings of the component.
 * @param {FlattenedItem[]} props.flattenedComponents - An array of flattened components of the component.
 * @param {object} props.settings - An object that represents the settings of the component.
 * @param {FlattenedItem[]} props.ancestors - An array of ancestor components of the component.
 *
 * @returns {JSX.Element} Returns a JSX element that renders the interface for editing the behavior settings of the component.
 */
function ComponentBehaviorPanel({
  comp,
  flattenedSettings,
  flattenedComponents,
  settings,
  ancestors,
}: IComponentBehaviorPanel) {
  const { accordions, setAccordions } = useBuilderAccordionContext()
  const { activeComponent, activeLayerClient } = useActiveLayerContext()

  const [localActiveItems, setLocalActiveItems] = useState<string[]>(() => {
    if (activeComponent && accordions?.[activeComponent.id]?.['behavior']) {
      return [accordions[activeComponent.id]['behavior']]
    }
    if (settings?.visibility && !settings?.custom) {
      return [ComponentBehaviorKey.Visibility]
    }
    return []
  })

  useLayoutEffect(() => {
    if (
      activeComponent &&
      settings?.visibility &&
      !settings?.custom &&
      !accordions?.[activeComponent.id]?.['behavior']
    ) {
      setAccordions('behavior', activeComponent.id, ComponentBehaviorKey.Visibility)
      setLocalActiveItems([ComponentBehaviorKey.Visibility])
    }
  }, [activeComponent, settings, accordions, setAccordions])

  const activeComponentId = activeComponent?.id || ''
  const component = activeLayerClient?.componentsMap?.get(activeComponentId)

  const value = component?.settings?.[ComponentBehaviorKey.Visibility] ?? null

  const updateValueRaw = useUpdateCompSettingValue(ComponentBehaviorKey.Visibility)

  // Wrapper untuk handle null value (always visible)
  const updateValue = (nextValue: ConditionValue | null) => {
    if (nextValue === null) {
      // Set to undefined untuk always visible
      updateValueRaw(undefined)
    } else {
      updateValueRaw(nextValue)
    }
  }

  const parentFormAncestor = ancestors?.find((a) => a.compId === 'form')
  const parentFormComponent = parentFormAncestor
    ? activeLayerClient?.componentsMap.get(parentFormAncestor.id)
    : undefined
  const parentFormId = parentFormComponent?._id

  const isForm = comp?.isForm
  const featureFlags = isForm || !!parentFormId ? COND_FEATURE_FLAG_ALL_WITH_FORM : COND_FEATURE_FLAG_ALL

  if (!component) return null

  return (
    <Accordion
      openMultiple={false}
      initialActiveItems={localActiveItems}
      onSelect={(opt) => {
        if (activeComponent) {
          setAccordions('behavior', activeComponent.id, opt)
          setLocalActiveItems([opt])
        }
      }}
    >
      {settings?.custom && (
        <ComponentSettingsAccordion
          activeComponentId={activeComponentId}
          comp={comp}
          wrap={false}
          settings={settings.custom}
          flattenedComponents={flattenedComponents}
          flattenedSettings={flattenedSettings}
          ancestors={ancestors}
          name='behavior'
        />
      )}
      {settings?.visibility && (
        <AccordionItem itemId={ComponentBehaviorKey.Visibility} title={<Trans>Visibility</Trans>}>
          <VisibilitySelector
            featureFlags={featureFlags}
            value={value as ConditionValue | null}
            onChange={updateValue}
            parentFormId={parentFormId}
          />
        </AccordionItem>
      )}
    </Accordion>
  )
}

export default ComponentBehaviorPanel
